import SEO, { breadcrumbSchema, faqSchema, serviceSchema } from "../components/SEO";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import BookingWidget from "../components/BookingWidget";
import FaqAccordion from "../components/FaqAccordion";
import Reveal from "../components/Reveal";
import { SectionHead } from "../components/Cards";
import { IMAGES } from "../data/site";
import { ALL_IMAGES } from "../data/content";
import { Check } from "lucide-react";

function ServicePage({ seoTitle, seoDesc, path, heroTitle, heroSub, image, blocks, bullets, faqs, bookTitle, crumbs }) {
  const schemas = [
    breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: crumbs, path }]),
    serviceSchema(heroTitle, seoDesc, path),
    ...(faqs.length ? [faqSchema(faqs)] : []),
  ];
  return (
    <>
      <SEO title={seoTitle} description={seoDesc} path={path} image={image} schemas={schemas} />
      <PageHero title={heroTitle} subtitle={heroSub} image={image} crumbs={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: crumbs }]} />
      <section className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          {blocks.map(([t, d]) => (
            <Reveal key={t}>
              <h2 className="font-heading text-2xl font-extrabold text-navy">{t}</h2>
              <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">{d}</p>
            </Reveal>
          ))}
          <Reveal>
            <div className="bg-royal-50/70 rounded-2xl p-6">
              <h3 className="font-heading font-bold text-navy">What's included</h3>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2 text-sm text-slate-700"><Check size={14} className="text-gold-dark" /> {b}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          {faqs.length > 0 && (
            <Reveal>
              <SectionHead center={false} eyebrow="Questions" title="FAQs" />
              <div className="mt-4 bg-white rounded-2xl border border-slate-200/80 px-6">
                <FaqAccordion faqs={faqs} idPrefix={path.slice(1)} />
              </div>
            </Reveal>
          )}
        </div>
        <div className="lg:sticky lg:top-28 h-fit">
          <BookingWidget compact title={bookTitle} />
        </div>
      </section>
      <CtaBand />
    </>
  );
}

export function Outstation() {
  return (
    <ServicePage
      seoTitle="Outstation Taxi Service in Jodhpur — Round Trips & Multi-Day"
      seoDesc="Outstation taxi from Jodhpur to anywhere in Rajasthan and beyond. Round trips & multi-day cabs with the same car and driver. Transparent quotes — Blue City Taxi."
      path="/outstation-taxi"
      heroTitle="Outstation Taxi From Jodhpur"
      heroSub="Long drives made easy — comfortable AC cars, highway-experienced drivers and flexible round-trip or multi-day plans across Rajasthan and neighbouring states."
      image={ALL_IMAGES.suvHighway}
      crumbs="Outstation Taxi"
      blocks={[
        ["Comfort for the Long Haul", "Outstation travel is different from a city hop — hours on the highway, changing terrain, luggage for days. Our cars are maintained for distance, and our drivers know the highways of Rajasthan, Gujarat and the Delhi corridor well enough to keep the journey smooth and safe."],
        ["Round Trip or Multi-Day, Your Way", "Book a simple there-and-back round trip, or keep the same car and driver for a multi-day journey — Jaisalmer desert nights, Udaipur lake mornings, Jaipur bazaars — with flexible halts wherever you want to stop, eat or photograph."],
        ["Honest, All-Inclusive Quotes", "We quote outstation fares with tolls, driver allowance and state taxes explained upfront — no mid-trip surprises. Multi-day plans include comfortable night arrangements for the driver so your mornings always start on time."],
      ]}
      bullets={["Same car & driver throughout", "Toll & tax explained upfront", "Flexible halts & detours", "All Rajasthan + Gujarat + Delhi NCR", "Sedan to tempo traveller options", "24/7 on-trip support"]}
      faqs={[
        { q: "How are outstation fares calculated?", a: "By route distance, vehicle type and trip duration. We quote the full fare before you book — WhatsApp your route and dates for the latest price." },
        { q: "Can the driver stay overnight with us?", a: "Yes, multi-day trips include the driver for the whole journey. Driver night allowance is included in the quote we share upfront." },
      ]}
      bookTitle="Book Outstation Cab"
    />
  );
}

export function AirportTaxi() {
  return (
    <ServicePage
      seoTitle="Jodhpur Airport Taxi — 24/7 Pickup & Drop"
      seoDesc="Jodhpur Airport (JDH) taxi service by Blue City Taxi: 24/7 pickups & drops, flight-timed scheduling, luggage help. Also Jaisalmer, Udaipur & Jaipur airport transfers. Call 094602 05326."
      path="/airport-taxi"
      heroTitle="Jodhpur Airport Taxi"
      heroSub="Landing at JDH or catching a dawn flight? We time your pickup to the flight, not the clock — 24 hours a day, 7 days a week."
      image={IMAGES.airport}
      crumbs="Airport Taxi"
      blocks={[
        ["Flight-Timed Pickups", "Share your flight number and we track the timing — early landing or delay, your driver adjusts. At arrivals, you'll find us ready with your name, help with luggage and a cool car waiting."],
        ["Drops That Never Miss", "For departures, we plan backwards from your flight time with a sensible buffer for Jodhpur traffic and airport security. Early morning and late-night departures are routine for us."],
        ["Beyond Jodhpur", "We also run airport transfers to and from Jaipur (JAI), Udaipur (UDR), Jaisalmer (JSA) and Kishangarh (KQH) airports — handy when flights are cheaper from a neighbouring city."],
      ]}
      bullets={["24/7 airport service", "Flight-timed scheduling", "Name-board pickup on request", "Luggage assistance", "All Rajasthan airports covered", "Clean AC cars, fixed pickup points"]}
      faqs={[
        { q: "How much is a taxi from Jodhpur Airport to the city?", a: "Fares depend on your drop location and vehicle. WhatsApp us your hotel or area and we quote the latest fare instantly." },
        { q: "My flight lands at 2 AM — can I get a cab?", a: "Yes, our airport service runs around the clock. Pre-book on WhatsApp with your flight details and the driver will be at arrivals." },
      ]}
      bookTitle="Book Airport Transfer"
    />
  );
}

export function LocalJodhpur() {
  return (
    <ServicePage
      seoTitle="Local Taxi Service in Jodhpur — Hourly & Full-Day Cabs"
      seoDesc="Local taxi service in Jodhpur by Blue City Taxi: hourly & full-day cabs, railway station pickup, hotel transfers, market runs & city sightseeing in clean AC cars. Call 094602 05326."
      path="/local-jodhpur-taxi"
      heroTitle="Local Taxi Service in Jodhpur"
      heroSub="Our home city, our speciality — hourly cabs, full-day rentals, station and hotel transfers, and sightseeing days through the Blue City."
      image={ALL_IMAGES.clocktower}
      crumbs="Local Jodhpur Taxi"
      blocks={[
        ["The City We Know Best", "Jodhpur is home. From the narrow blue lanes below Mehrangarh to the newer neighbourhoods across the bypass, our drivers know where the car can go, where to park, and the fastest honest route at any hour."],
        ["Hourly & Full-Day Packages", "Need a cab for a few errands, a wedding function, or a full day of sightseeing? Book by the hour or the day — the car stays with you, waits for you, and moves when you're ready."],
        ["Station, Hotel & Home Pickups", "Timed pickups from Jodhpur Junction, transfers between hotels and havelis, hospital runs, school and office drops — everyday Jodhpur travel handled with the same care as a grand tour."],
      ]}
      bullets={["Hourly & full-day packages", "Jodhpur Junction pickups", "Hotel & home door-to-door", "Blue City sightseeing routes", "Elder & family friendly drivers", "24/7 availability"]}
      faqs={[
        { q: "Can I book a cab for the whole day in Jodhpur?", a: "Yes — full-day (8 hr / 80 km style) packages are our most-booked local option. Share your plan on WhatsApp for today's availability and fare." },
        { q: "Do you cover areas outside the old city?", a: "Yes — all of Jodhpur city and surroundings, including Jhalamand, Pal Road, Sardarpura, Ratanada, Mandore and Osian-side trips." },
      ]}
      bookTitle="Book Local Cab"
    />
  );
}
